// src/components/Login.js
import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import sessionStore from '../stores/sessionStore'
import { useNavigate } from 'react-router-dom'
import {
	TextField,
	Button,
	Typography,
	Link,
	Paper,
	Container,
	Alert,
	CircularProgress,
	Box,
	Divider
} from '@mui/material'
import { Google as GoogleIcon } from '@mui/icons-material'
import { FaWhatsapp } from "react-icons/fa"
import { RiTelegram2Line } from "react-icons/ri"
import logger from '../utils/logger'
import { auth } from '../firebaseConfig'

function Login() {
	const navigate = useNavigate()
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [error, setError] = useState('')
	const [loading, setLoading] = useState(false)
	const [authChecked, setAuthChecked] = useState(false)

	logger.log('LOGIN компонент загружен')

	// Проверяем инициализацию Firebase Auth
	useEffect(() => {
		const unsubscribe = auth.onAuthStateChanged(async (user) => {
			logger.log(`Firebase Auth State Changed in Login: ${user ? 'User exists' : 'No user'}`);
			
			if (user) {
				try {
					setLoading(true);
					// Восстанавливаем сессию и данные пользователя
					await sessionStore.restoreSession();
					
					if (sessionStore.user) {
						// Если пользователь в сторе, перенаправляем
						navigate('/', { replace: true });
					}
				} catch (error) {
					logger.error('Ошибка восстановления сессии в Login:', error);
				} finally {
					setLoading(false);
				}
			}
			
			setAuthChecked(true);
			// Не отписываемся от Firebase Auth, чтобы слушать изменения
		});
		
		// Отписываемся при размонтировании компонента
		return () => unsubscribe();
	}, [navigate]);

	// Сохраняем в локальное хранилище только после проверки авторизации
	useEffect(() => {
		if (!authChecked) return;
		
		if (!sessionStore.user) {
			// Retrieve email from localStorage if available
			const storedEmail = localStorage.getItem('userEmail')
			if (storedEmail) {
				setEmail(storedEmail)
			}
		}
	}, [authChecked, sessionStore.user]);

	const handleSubmit = async e => {
		e.preventDefault()
		setError('')
		setLoading(true)

		try {
			await sessionStore.loginByEmail(email, password)
		} catch (err) {
			logger.error(err)
			setError(err.message || 'Ошибка авторизации')
		} finally {
			setLoading(false)
		}
	}

	const handleGoogleLogin = async () => {
		setError('')
		setLoading(true)
		try {
			await sessionStore.loginByGoogle()
		} catch (err) {
			logger.error(err)
			setError(err.message || 'Ошибка авторизации через Google')
		} finally {
			setLoading(false)
		}
	}

	if (!authChecked || loading) {
		return (
			<Container component='main' maxWidth='xs' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
				<CircularProgress />
				<div style={{ marginLeft: '10px' }}>
					{!authChecked ? 'Проверка авторизации...' : 'Вход в систему...'}
				</div>
			</Container>
		)
	}

	// Если пользователь уже авторизован, сразу перенаправляем
	if (sessionStore.user) {
		navigate('/', { replace: true });
		return null;
	}

	return (
		<Container component='main' maxWidth='xs'>
		
			<Paper elevation={3} style={{ padding: 20, marginTop: 40 }}>
			<Typography variant='h6' style={{ marginBottom: 20 }} sx={{color: 'text.primary'}}>
						Calc5 - система расчета стоимости душевых кабин. <br />
						Модуль Производителя
					</Typography>
					<Typography variant="body1" style={{ marginBottom: 20, textAlign: 'center' }}>
          Вход в систему
        </Typography>
				{error && (
					<Alert severity='error' style={{ marginBottom: 20 }}>
						{error}
					</Alert>
				)}
				<form onSubmit={handleSubmit}>
					<TextField
						variant='outlined'
						margin='normal'
						required
						fullWidth
						label={'Email'}
						autoComplete='email'
						autoFocus
						value={email}
						size='small'
						onChange={e => setEmail(e.target.value)}
					/>
					<TextField
						variant='outlined'
						margin='normal'
						required
						fullWidth
						label={'Пароль'}
						type='password'
						autoComplete='current-password'
						value={password}
						size='small'
						onChange={e => setPassword(e.target.value)}
					/>
					<Button
						type='submit'
						fullWidth
						variant='contained'
						color='primary'
						style={{ margin: '24px 0 16px' }}
						disabled={loading}
					>
						Войти
					</Button>
				</form>
				<Button
					fullWidth
					variant='outlined'
					color='primary'
					startIcon={<GoogleIcon />}
					onClick={handleGoogleLogin}
					style={{ marginBottom: 16 }}
					disabled={loading}
				>
					Войти через Google
				</Button>
				<Typography>
					<Link href='/forgot' variant='body2'>
						Забыли пароль?
					</Link>
				</Typography>
				<Typography style={{ marginBottom: 20 }}>
					Нет учетной записи?{' '}
					<Link href='/register' variant='body2'>
						Зарегистрироваться
					</Link>
				</Typography>
				
				<Divider sx={{ my: 2 }} />
				
				
			
			</Paper>
		</Container>
	)
}

export default observer(Login)
